import React, { useEffect, useRef, useState } from "react";
import LoadingSpinner from "./LoadingSpinner";

const AddCustomersPopup = ({
  allCustomers,
  customers,
  selectedCustomers,
  handleCustomerSelect,
  handleAddCustomers,
  handleSelectAll,
  setShowPopup,
  loading,
}) => {
  const popupRef = useRef();
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.classList.contains("popup-overlay")) {
        setShowPopup(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [setShowPopup]);

  // Filter out customers already in the group
  const nonGroupCustomers = allCustomers.filter(
    (customer) => !customers.some((c) => c.id === customer.id)
  );

  const areAllSelected =
    nonGroupCustomers.length > 0 &&
    nonGroupCustomers.every((c) => selectedCustomers.includes(c.id));

  // Sort so non-group customers appear first
  const sortedCustomers = [...allCustomers].sort((a, b) => {
    const aInGroup = customers.some((c) => c.id === a.id);
    const bInGroup = customers.some((c) => c.id === b.id);
    return aInGroup - bInGroup;
  });

  // 🔍 Filter customers by search
  const filteredCustomers = sortedCustomers.filter((customer) => {
    const search = searchTerm.toLowerCase();
    return (
      customer.name?.toLowerCase().includes(search) ||
      customer.phoneNumber?.toLowerCase().includes(search) ||
      customer.location?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="popup-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        ref={popupRef}
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h3 className="text-xl font-semibold">Select Customers</h3>
          {/* 🔍 Search Input */}
          <div className="relative w-full sm:w-72">
            <input
              type="text"
              placeholder="Search by name, phone, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 text-sm"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {/* Desktop View */}
          <div className="hidden md:block h-full p-6">
            <div className="overflow-auto h-full max-h-[60vh]">
              <table className="w-full text-left table-auto text-sm">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 w-16">
                      <input
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={areAllSelected}
                        className="w-4 h-4 accent-blue-600 cursor-pointer"
                      />
                    </th>
                    <th className="px-4 py-3 font-medium text-gray-900 min-w-[150px]">
                      Name
                    </th>
                    <th className="px-4 py-3 font-medium text-gray-900 min-w-[130px]">
                      Phone
                    </th>
                    <th className="px-4 py-3 font-medium text-gray-900 min-w-[120px]">
                      Location
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((customer) => {
                    const alreadyInGroup = customers.some(
                      (c) => c.id === customer.id
                    );
                    const isSelected = selectedCustomers.includes(customer.id);
                    return (
                      <tr
                        key={customer.id}
                        className={`border-b border-gray-100 transition-colors ${
                          alreadyInGroup
                            ? "bg-gray-50 opacity-60"
                            : "hover:bg-gray-50"
                        } ${isSelected ? "bg-blue-50" : ""}`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              if (!alreadyInGroup)
                                handleCustomerSelect(customer.id);
                            }}
                            disabled={alreadyInGroup}
                            className="w-4 h-4 accent-blue-600 cursor-pointer disabled:opacity-50"
                          />
                        </td>
                        <td className="px-4 py-3 text-gray-900">
                          <div className="font-medium">{customer.name}</div>
                          {alreadyInGroup && (
                            <div className="text-xs text-gray-500">
                              Already in group
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {customer.phoneNumber}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {customer.location || "--"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {filteredCustomers.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No matching customers found
                </div>
              )}
            </div>
          </div>

          {/* Mobile View */}
          <div className="md:hidden h-full p-4">
            {/* Select All Button */}
            <div className="mb-4 p-3 bg-gray-100 rounded-lg">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={areAllSelected}
                  className="w-4 h-4 accent-blue-600 cursor-pointer"
                />
                <span className="text-sm font-medium">
                  Select All Available
                </span>
              </label>
            </div>

            {/* Customer Cards */}
            <div className="space-y-3 overflow-y-auto max-h-[50vh]">
              {filteredCustomers.map((customer) => {
                const alreadyInGroup = customers.some(
                  (c) => c.id === customer.id
                );
                const isSelected = selectedCustomers.includes(customer.id);
                return (
                  <div
                    key={customer.id}
                    className={`border rounded-lg p-3 transition-colors ${
                      alreadyInGroup ? "bg-gray-100 opacity-60" : "bg-white"
                    } ${
                      isSelected
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200"
                    }`}
                  >
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {
                          if (!alreadyInGroup)
                            handleCustomerSelect(customer.id);
                        }}
                        disabled={alreadyInGroup}
                        className="w-4 h-4 accent-blue-600 mt-1 cursor-pointer disabled:opacity-50"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900">
                          {customer.name}
                        </div>
                        <div className="text-sm text-gray-600">
                          {customer.phoneNumber}
                        </div>
                        <div className="text-sm text-gray-500">
                          {customer.location || "No location"}
                        </div>
                        {alreadyInGroup && (
                          <div className="text-xs text-gray-500 font-medium">
                            Already in group
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                );
              })}

              {filteredCustomers.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No matching customers found
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row sm:justify-between space-y-2 sm:space-y-0 sm:space-x-2">
            <button
              onClick={handleAddCustomers}
              className={`px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors ${
                loading || selectedCustomers.length === 0
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
              disabled={loading || selectedCustomers.length === 0}
            >
              {loading ? (
                <LoadingSpinner size="sm" color="white" text="Adding..." />
              ) : (
                `Add ${
                  selectedCustomers.length > 0
                    ? `(${selectedCustomers.length})`
                    : ""
                } to Group`
              )}
            </button>
            <button
              onClick={() => setShowPopup(false)}
              className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 font-medium transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCustomersPopup;
